import {
  KoButton,
  KoForm,
  KoLoader,
  KoModal,
  KoTable,
  KoText,
  KoTitle,
  KoToast,
  KoEmpty,
  KoPagination
} from 'packages';
import KoIcon from '@kwapio/icons';
import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { add, remove } from 'shared/store/breadcrumbSlice';
import KoToolBar from 'shared/components/toolbar';
import { ApiPlatformAPI } from '../services/apiplatform.service';
import { COLUMNS_SERVICES } from '../constants';
import ServiceCrud from './crud/ServiceCrud';
import ApiSubNav from './ApiSubNav';
import styles from './apiplatform.module.scss';

const MOCK_SERVICES = [
  { _id: 'svc-001', name: 'User Service', description: 'Core user management and authentication', version: 'v3.2.1', status: 'active', protocol: 'REST', baseUrl: 'https://api.kwapio.com/v3/users', endpointsCount: 24, owner: 'Auth Team', updatedAt: '2026-02-24T10:30:00Z' },
  { _id: 'svc-002', name: 'Payment Gateway', description: 'Payment processing and subscription billing', version: 'v4.1.0', status: 'active', protocol: 'REST', baseUrl: 'https://api.kwapio.com/v4/payments', endpointsCount: 18, owner: 'Billing Team', updatedAt: '2026-02-23T16:45:00Z' },
  { _id: 'svc-003', name: 'Notification Hub', description: 'Multi-channel notification delivery (email, push, SMS)', version: 'v1.5.3', status: 'active', protocol: 'REST', baseUrl: 'https://api.kwapio.com/v1/notifications', endpointsCount: 12, owner: 'Platform Team', updatedAt: '2026-02-22T09:15:00Z' },
  { _id: 'svc-004', name: 'Search Engine', description: 'Full-text search and indexing service', version: 'v2.3.1', status: 'active', protocol: 'REST', baseUrl: 'https://api.kwapio.com/v2/search', endpointsCount: 8, owner: 'Data Team', updatedAt: '2026-02-24T08:00:00Z' },
  { _id: 'svc-005', name: 'Analytics Pipeline', description: 'Real-time event tracking and analytics', version: 'v2.1.0', status: 'active', protocol: 'gRPC', baseUrl: 'grpc://analytics.kwapio.internal:9090', endpointsCount: 15, owner: 'Data Team', updatedAt: '2026-02-21T14:20:00Z' },
  { _id: 'svc-006', name: 'File Storage', description: 'Document upload, storage, and CDN delivery', version: 'v1.9.4', status: 'active', protocol: 'REST', baseUrl: 'https://api.kwapio.com/v1/files', endpointsCount: 10, owner: 'Platform Team', updatedAt: '2026-02-20T11:30:00Z' },
  { _id: 'svc-007', name: 'Legacy Billing (v1)', description: 'Deprecated billing service — migrating to Payment Gateway', version: 'v1.0.8', status: 'deprecated', protocol: 'REST', baseUrl: 'https://api.kwapio.com/v1/billing', endpointsCount: 6, owner: 'Billing Team', updatedAt: '2026-01-15T09:00:00Z' }
];

function Services() {
  const dispatch = useDispatch();
  const { workspaceId } = useParams();

  const [services, setServices] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [isLoading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({ size: 20, page: 1 });

  const [isModalOpen, setModalOpen] = useState(false);
  const [isDeleteOpen, setDeleteOpen] = useState(false);
  const [currentRecord, setCurrentRecord] = useState(null);
  const [form] = KoForm.useForm();

  const basePath = workspaceId
    ? `/workspace/${workspaceId}/development/apiplatform`
    : '/apiplatform';

  useEffect(() => {
    dispatch(remove());
    dispatch(add([
      { title: 'Home', path: 'home/myoverview' },
      { title: 'API Platform', path: basePath },
      { title: 'Services' }
    ]));
  }, [workspaceId]);

  useEffect(() => {
    fetchServices();
  }, [workspaceId, pagination]);

  const fetchServices = () => {
    setLoading(true);
    ApiPlatformAPI.getServices({ page: pagination.page, limit: pagination.size }).then(
      (res) => {
        const data = res?.data?.length ? res.data : MOCK_SERVICES;
        setServices(data);
        setTotalCount(res?.totalCount || data.length);
        setLoading(false);
      },
      () => { setServices(MOCK_SERVICES); setTotalCount(MOCK_SERVICES.length); setLoading(false); }
    );
  };

  const handleSave = (values) => {
    setLoading(true);
    ApiPlatformAPI.createService(values, currentRecord?._id).then(
      () => {
        setLoading(false);
        setModalOpen(false);
        form.resetFields();
        setCurrentRecord(null);
        fetchServices();
        KoToast.success(currentRecord ? 'Service updated.' : 'Service registered.');
      },
      () => { setLoading(false); KoToast.error('Failed to save service.'); }
    );
  };

  const handleDelete = () => {
    setLoading(true);
    ApiPlatformAPI.deleteService(currentRecord._id || currentRecord.serviceId).then(
      () => {
        setLoading(false);
        setDeleteOpen(false);
        setCurrentRecord(null);
        fetchServices();
        KoToast.success('Service deleted.');
      },
      () => { setLoading(false); KoToast.error('Delete failed.'); }
    );
  };

  const columnsWithActions = [
    ...COLUMNS_SERVICES,
    {
      label: '', id: 'actions', key: 'actions', width: 100,
      render: (_, record) => (
        <div style={{ display: 'flex', gap: '4px' }}>
          <KoButton
            iconBtn
            icon={<KoIcon name='EditOutlined' width={14} height={14} />}
            onClick={(e) => { e.stopPropagation(); setCurrentRecord(record); form.setFieldsValue(record); setModalOpen(true); }}
          />
          <KoButton
            iconBtn
            danger
            icon={<KoIcon name='DeleteOutlined' width={14} height={14} />}
            onClick={(e) => { e.stopPropagation(); setCurrentRecord(record); setDeleteOpen(true); }}
          />
        </div>
      )
    }
  ];

  return (
    <div className='tableCard'>
      {isLoading && <KoLoader />}
      <ApiSubNav activeKey='services' />

      <KoToolBar
        title='API Services'
        search={{ visible: true, placeholder: 'Search services...', onChange: () => {} }}
        filter={{ visible: false, options: [] }}
        buttonList={[{
          label: 'Register Service',
          type: 'primary',
          icon: <KoIcon color='#FFF' name='PlusOutlined' width={18} height={18} />,
          onClick: () => { setCurrentRecord(null); form.resetFields(); setModalOpen(true); }
        }]}
      />

      {services.length === 0 && !isLoading && <KoEmpty description='No API services registered' />}
      {services.length > 0 && (
        <>
          <KoTable maxHeight={480} dataSource={services} columns={columnsWithActions} rowKey={(r) => r._id || r.serviceId} />
          <KoPagination totalCount={totalCount} pageSize={pagination.size} currentPage={pagination.page} onChange={(page, size) => setPagination({ page, size })} />
        </>
      )}

      <KoModal
        title={currentRecord ? 'Edit Service' : 'Register Service'}
        centered
        open={isModalOpen}
        handleOk={() => setModalOpen(false)}
        onClose={() => { setModalOpen(false); setCurrentRecord(null); }}
        width={'min(700px, 95vw)'}
        footer={[
          <KoButton key='cancel' label='Cancel' onClick={() => { setModalOpen(false); setCurrentRecord(null); }} />,
          <KoButton key='save' type='primary' loading={isLoading} label='Save' onClick={() => form.submit()} />
        ]}
      >
        <ServiceCrud form={form} handleFinish={handleSave} />
      </KoModal>

      <KoModal
        title='Delete Service'
        centered
        open={isDeleteOpen}
        handleOk={() => setDeleteOpen(false)}
        onClose={() => { setDeleteOpen(false); setCurrentRecord(null); }}
        width={'min(440px, 95vw)'}
        footer={[
          <KoButton key='cancel' label='Cancel' onClick={() => { setDeleteOpen(false); setCurrentRecord(null); }} />,
          <KoButton key='delete' type='primary' danger loading={isLoading} label='Delete' onClick={handleDelete} />
        ]}
      >
        <div className={styles.confirmBody}>
          <KoTitle level={5} text={`Are you sure you want to delete "${currentRecord?.name}"?`} />
          <KoText text='This action cannot be undone.' />
        </div>
      </KoModal>
    </div>
  );
}

export default Services;
